import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import apiCoreAdapter from '../../../../hooks/apiCoreAdapter';
import { deleteBlockage } from '../../../../hooks/api/blockage';
import { getSessionData } from '../../../../hooks/api';
import { getImpersonatorId, isImpersonated } from '../../../../hooks/api/auth/tokenManager';
import { localTzToUTC } from '../timeZoneHelpers';
import {
  invalidateSessionsCache,
  invalidateSchedulesCache,
  invalidatePatientsCache,
} from '../data';
import { getRealSessionId } from '../../../../pages/psychologist/tabs/calendar/utils/getRealSessionId';
import type {
  SessionWithPsychologist,
  FullDayRecurrence,
  ScheduleDirectInput,
  DateSpecificSlot,
  Patient,
} from './useCalendarData';
import type { AppointmentType } from '../../../../contexts/PsychologistContext';

import { activityTracker } from "../../../../utils/activityTracker";
// ---------------------------------------------------------------------------
// Options & Return types
// ---------------------------------------------------------------------------

export interface UseCalendarRescheduleOptions {
  psychologist: { id: string | number; specialties: string[] };
  psychologistId: string | null | undefined;
  selectedTimeZone: string;
  startOfWeek: Date;
  sessions: SessionWithPsychologist[];
  setSessions: React.Dispatch<React.SetStateAction<SessionWithPsychologist[]>>;
  fullDayRecurrences: FullDayRecurrence[];
  selectedSession: SessionWithPsychologist | null;
  setSelectedSession: React.Dispatch<React.SetStateAction<SessionWithPsychologist | null>>;
  setIsSessionModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedAppointmentType: AppointmentType | null;
  sessionsCacheByWeekRef: React.MutableRefObject<Map<string, SessionWithPsychologist[]>>;
  pendingFetchesRef: React.MutableRefObject<Map<string, Promise<unknown>>>;
  availabilityCacheRef: React.MutableRefObject<Map<string, SessionWithPsychologist[]>>;
  dateSpecificSlotsCacheRef: React.MutableRefObject<Map<string, DateSpecificSlot[]>>;
  availabilityPendingRef: React.MutableRefObject<Map<string, Promise<SessionWithPsychologist[]>>>;
  recurrencesCacheRef: React.MutableRefObject<FullDayRecurrence[] | undefined>;
  recurrencesCachePsychIdRef: React.MutableRefObject<string | undefined>;
  patientsCacheRef: React.MutableRefObject<Patient[] | undefined>;
  patientsCachePsychIdRef: React.MutableRefObject<string | undefined>;
  scheduleDirect: (input: ScheduleDirectInput) => { ok: boolean };
  loadData: () => Promise<void>;
  showError: (msg: string) => void;
  showSuccess: (msg: string) => void;
  onRescheduleModeChange?: (isReschedule: boolean, session: SessionWithPsychologist | null) => void;
}

export interface UseCalendarRescheduleReturn {
  isRescheduling: boolean;
  reschedulingSession: SessionWithPsychologist | null;
  setReschedulingSession: React.Dispatch<React.SetStateAction<SessionWithPsychologist | null>>;
  isPreparingReschedule: boolean;
  setIsPreparingReschedule: React.Dispatch<React.SetStateAction<boolean>>;
  isRescheduleMode: boolean;
  unblockConfirm: { psychologistId: string; fromDate: Date; toDate: Date; sesionId: string; blockageIds?: string[] } | null;
  setUnblockConfirm: React.Dispatch<React.SetStateAction<{ psychologistId: string; fromDate: Date; toDate: Date; sesionId: string; blockageIds?: string[] } | null>>;
  unblockingSessionId: string | null;
  handleRefreshScheduledSession: () => Promise<void>;
  handleSessionClickMemo: (session: SessionWithPsychologist) => void;
  handleSessionUnblockMemo: (psychologistId: string, fromDate: Date, toDate: Date, sesionId: string, blockageIds?: string[]) => void;
  handleUnblockConfirmed: (psychologistId: string, fromDate: Date, toDate: Date, sesionId: string, blockageIds?: string[]) => Promise<void>;
  handleRescheduleSession: (
    sessionId: string,
    newDate: string,
    newTime: string,
    processId?: string,
    reason?: { motiveId?: string; note?: string; initiatedBy?: string },
  ) => Promise<void>;
}

// ---------------------------------------------------------------------------
// Hook implementation
// ---------------------------------------------------------------------------

export function useCalendarReschedule({
  psychologist,
  psychologistId,
  startOfWeek,
  sessions,
  setSessions,
  fullDayRecurrences,
  selectedSession,
  setSelectedSession,
  setIsSessionModalOpen,
  selectedAppointmentType,
  sessionsCacheByWeekRef,
  pendingFetchesRef,
  availabilityCacheRef,
  dateSpecificSlotsCacheRef,
  availabilityPendingRef,
  recurrencesCacheRef,
  recurrencesCachePsychIdRef,
  patientsCacheRef,
  patientsCachePsychIdRef,
  scheduleDirect,
  loadData,
  showError,
  showSuccess,
  onRescheduleModeChange,
  selectedTimeZone,
}: UseCalendarRescheduleOptions): UseCalendarRescheduleReturn {
  // -------------------------------------------------------------------------
  // State
  // -------------------------------------------------------------------------
  const [isRescheduling, setIsRescheduling] = useState<boolean>(false);
  const [reschedulingSession, setReschedulingSession] = useState<SessionWithPsychologist | null>(null);
  const [isPreparingReschedule, setIsPreparingReschedule] = useState<boolean>(false);
  const [unblockConfirm, setUnblockConfirm] = useState<{ psychologistId: string; fromDate: Date; toDate: Date; sesionId: string; blockageIds?: string[] } | null>(null);
  const [unblockingSessionId, setUnblockingSessionId] = useState<string | null>(null);

  // -------------------------------------------------------------------------
  // Computed
  // -------------------------------------------------------------------------
  const isRescheduleMode = !!reschedulingSession;

  // -------------------------------------------------------------------------
  // Refs
  // -------------------------------------------------------------------------
  const onRescheduleModeChangeRef = useRef(onRescheduleModeChange);
  onRescheduleModeChangeRef.current = onRescheduleModeChange;

  // -------------------------------------------------------------------------
  // Effects
  // -------------------------------------------------------------------------

  // Notify parent about reschedule mode changes (ref to avoid re-render loops)
  useEffect(() => {
    onRescheduleModeChangeRef.current?.(isRescheduleMode, reschedulingSession);
  }, [isRescheduleMode, reschedulingSession]);

  // Sync selectedSession with latest sessions data (e.g. when "scheduled" becomes "active")
  useEffect(() => {
    if (!selectedSession || selectedSession.status !== 'scheduled') return;
    const updated = sessions.find(
      s =>
        s.date === selectedSession.date &&
        s.time === selectedSession.time &&
        s.patient.email === selectedSession.patient.email
    );
    if (updated && updated.status !== 'scheduled') {
      setSelectedSession(updated);
    }
  }, [sessions, selectedSession, setSelectedSession]);

  // -------------------------------------------------------------------------
  // Handlers
  // -------------------------------------------------------------------------

  const handleRefreshScheduledSession = useCallback(async () => {
    try {
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      const weekKey = `${startOfWeek.toISOString().slice(0, 10)}_${endOfWeek.toISOString().slice(0, 10)}_${psychologist.id}`;
      invalidateSessionsCache(weekKey);
      await loadData();
    } catch { /* noop */ }
  }, [startOfWeek, psychologist.id, loadData]);

  const handleSessionClickMemo = useCallback(
    (session: SessionWithPsychologist) => {
      if (!session.process_id) {
        console.warn('⚠️ Session clicked without process_id', {
          sessionId: session.id,
          date: session.date,
          time: session.time,
          session,
        });
      }
      if (session.status === 'blocked') return;
      setSelectedSession(session);
      setIsSessionModalOpen(true);
    },
    [setSelectedSession, setIsSessionModalOpen]
  );

  const handleSessionUnblockMemo = useCallback(
    (psychologistId: string, fromDate: Date, toDate: Date, sesionId: string, blockageIds?: string[]) => {
      setUnblockConfirm({ psychologistId, fromDate, toDate, sesionId, blockageIds });
    },
    []
  );

  const handleUnblockConfirmed = useCallback(
    async (psychologistId: string, fromDate: Date, toDate: Date, sesionId: string, blockageIds?: string[]) => {
      setUnblockingSessionId(sesionId);
      try {
        const isFullDayBlocked = sesionId.startsWith('blocked-full-day-');

        if (isFullDayBlocked) {
          const dateString = sesionId.replace('blocked-full-day-', '');

          let recurrence = fullDayRecurrences.find(rec => rec.date === dateString);

          if (!recurrence || !recurrence.recurrenceId) {
            try {
              const recurrencesResponse = await apiCoreAdapter.getOccupationRecurrences(String(psychologistId));
              if (recurrencesResponse.success && recurrencesResponse.recurrences) {
                const foundRec = recurrencesResponse.recurrences.find(rec => {
                  if (rec.is_full_day && rec.start_time) {
                    try {
                      const date = new Date(rec.start_time);
                      const recDateString = date.toISOString().split('T')[0];
                      return recDateString === dateString;
                    } catch {
                      return false;
                    }
                  }
                  return false;
                });

                if (foundRec) {
                  recurrence = {
                    date: dateString,
                    reason: foundRec.reason || 'Día bloqueado',
                    recurrenceId: foundRec.id,
                  };
                }
              }
            } catch (error) {
              console.error('Error obteniendo recurrencias:', error);
            }
          }

          if (recurrence && recurrence.recurrenceId) {
            const response = await apiCoreAdapter.updateOccupationRecurrence(
              psychologistId,
              recurrence.recurrenceId,
              { deleted_at: true }
            );

            if (response.success) {
              toast.success('Ocupacion del dia eliminada correctamente');

              const endOfWeek = new Date(startOfWeek);
              endOfWeek.setDate(startOfWeek.getDate() + 6);
              const weekKey = `${startOfWeek.toISOString().slice(0, 10)}_${endOfWeek.toISOString().slice(0, 10)}_${psychologist.id}`;

              recurrencesCacheRef.current = undefined;
              recurrencesCachePsychIdRef.current = undefined;
              const recurrencesKey = `recurrences:${psychologistId}`;
              pendingFetchesRef.current.delete(recurrencesKey);

              sessionsCacheByWeekRef.current.delete(weekKey);
              const key = `sessions:${weekKey}`;
              pendingFetchesRef.current.delete(key);

              setSessions(prev => prev.filter(session => session.id !== sesionId));

              invalidateSessionsCache(weekKey);
              if (psychologistId) {
                invalidateSchedulesCache(String(psychologistId));
              }

              availabilityCacheRef.current.delete(weekKey);
              dateSpecificSlotsCacheRef.current.delete(weekKey);
              availabilityPendingRef.current.delete(weekKey);

              await loadData();
            } else {
              toast.error(response.message || 'Error al eliminar la ocupacion del dia.');
            }
          } else {
            toast.error('No se pudo encontrar la recurrencia para eliminar');
          }
        } else {
          // Buscar si existe una recurrencia de rango horario para este weekday Y horario.
          // Comparar sólo el weekday no alcanza: si la psicóloga tiene CUALQUIER recurrencia
          // semanal ese día (ej. una reunión fija los jueves 10-11), y ella intenta desbloquear
          // un bloqueo puntual distinto en el mismo weekday (ej. jueves 13-14), este match
          // borraría la recurrencia equivocada. El toast de éxito se dispara igual (esa
          // eliminación sí ocurre), pero el bloqueo puntual real nunca se borra del backend
          // y reaparece en el próximo refetch.
          let matchingRecurrenceId: string | null = null;
          try {
            const recurrencesResponse = await apiCoreAdapter.getOccupationRecurrences(String(psychologistId));
            if (recurrencesResponse.success && recurrencesResponse.recurrences) {
              const slotWeekday = fromDate.getDay(); // 0=Sun, 6=Sat
              const toHHMM = (d: Date) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
              const slotStart = toHHMM(fromDate);
              const slotEnd = toHHMM(toDate);
              const found = recurrencesResponse.recurrences.find(
                rec =>
                  !rec.is_full_day &&
                  rec.weekday === slotWeekday &&
                  rec.start_time === slotStart &&
                  rec.end_time === slotEnd
              );
              if (found) {
                matchingRecurrenceId = found.id;
              }
            }
          } catch (error) {
            console.error('Error buscando recurrencia para desbloqueo:', error);
          }

          let success = false;

          if (matchingRecurrenceId) {
            const response = await apiCoreAdapter.updateOccupationRecurrence(
              psychologistId,
              matchingRecurrenceId,
              { deleted_at: true }
            );
            success = response.success;
            if (!success) {
              toast.error(response.message || 'Error al eliminar el bloqueo fijo.');
            }
          } else {
            // Bloqueo puntual, usar el nuevo endpoint de blockages.
            // Se identifica por `ids` (los IDs reales de los slots de 30min asignados por
            // el backend) además de fecha/horario — fecha+horario no alcanza como único
            // identificador cuando la psicóloga tiene dos bloqueos iguales el mismo día.
            try {
              const utcDate = fromDate.toISOString().split('T')[0]; // YYYY-MM-DD
              const utcStartTime = fromDate.toISOString().split('T')[1].substring(0, 5); // HH:MM
              const utcEndTime = toDate.toISOString().split('T')[1].substring(0, 5); // HH:MM

              const response = await deleteBlockage({
                date: utcDate,
                start_time: utcStartTime,
                end_time: utcEndTime,
                ids: blockageIds,
              });

              // No confiar en que la promesa haya resuelto sin error: el backend puede
              // responder 200 sin haber modificado nada (total 0), por ejemplo si el rango
              // fecha/horario no identificó el registro o si el horario estaba ocupado por
              // algo que no es un bloqueo manual (vacaciones). Sin este chequeo, se mostraba
              // éxito y se sacaba el bloqueo de la vista aunque siguiera persistido —
              // reaparecía recién al refrescar la página.
              // api-core responde `total` (restaurados + borrados + creados); este chequeo
              // leía `deleted_count`, que nunca viene, así que `undefined !== 0` daba éxito
              // siempre. `deleted_count` queda como respaldo por si otro backend lo manda.
              const affectedSlots = response.total ?? response.deleted_count;
              success = affectedSlots !== 0;
              if (!success) {
                toast.error('No se pudo confirmar la eliminación del bloqueo. Volvé a intentarlo.');
              }
            } catch (error) {
              success = false;
              toast.error('Error al desbloquear horario');
            }
          }

          if (success) {
            toast.success('Horarios desbloqueados correctamente');

            const endOfWeek = new Date(startOfWeek);
            endOfWeek.setDate(startOfWeek.getDate() + 6);
            const weekKey = `${startOfWeek.toISOString().slice(0, 10)}_${endOfWeek.toISOString().slice(0, 10)}_${psychologist.id}`;

            recurrencesCacheRef.current = undefined;
            recurrencesCachePsychIdRef.current = undefined;
            const recurrencesKey = `recurrences:${psychologistId}`;
            pendingFetchesRef.current.delete(recurrencesKey);

            sessionsCacheByWeekRef.current.delete(weekKey);
            const key = `sessions:${weekKey}`;
            pendingFetchesRef.current.delete(key);

            invalidateSessionsCache(weekKey);
            if (psychologistId) {
              invalidateSchedulesCache(String(psychologistId));
            }

            availabilityCacheRef.current.clear();
            dateSpecificSlotsCacheRef.current.clear();
            availabilityPendingRef.current.clear();

            setSessions(prev => prev.filter(session => session.id !== sesionId));

            await loadData();
          }
        }
      } finally {
        setUnblockingSessionId(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fullDayRecurrences]
  );

  const handleRescheduleSession = async (
    sessionId: string,
    newDate: string,
    newTime: string,
    processId?: string,
    reason?: { motiveId?: string; note?: string; initiatedBy?: string }
  ): Promise<void> => {
    const sourceSession = reschedulingSession || selectedSession;

    const finalProcessId = processId || sourceSession?.process_id;

    if (!finalProcessId) {
      console.error('process_id missing', {
        processId,
        sourceSessionProcessId: sourceSession?.process_id,
        sessionId,
        sourceSession,
      });
      showError('No se puede reprogramar: falta el ID del proceso. Por favor, recarga la página e intenta nuevamente.');
      throw new Error('Información incompleta: process_id no está disponible');
    }

    if (!psychologistId) {
      console.error('psychologistId is missing', { sessionId });
      showError('No se puede reprogramar: falta el ID del psicólogo.');
      throw new Error('Información incompleta: psychologistId no está disponible');
    }

    setIsRescheduling(true);
    setIsSessionModalOpen(false);

    try {
      // Paso 1: Obtener el ID real de la sesión si es temporal
      let realSessionId = sessionId;
      try {
        realSessionId = await getRealSessionId(
          sessionId,
          sourceSession?.date,
          sourceSession?.time,
          sourceSession?.patient?.email
        );
      } catch (error) {
        showError('No se pudo encontrar la sesión. Por favor, recarga la página e intenta nuevamente.');
        throw error;
      }

      // Paso 2: Cancelar la sesión actual con razón de reprogramación.
      // Mantenemos cancellation_type 'rescheduled' para el tracking, y si la psicóloga
      // eligió un motivo en el modal, lo enviamos (motive_id + nota + quién lo inició).
      await apiCoreAdapter.cancelSession(realSessionId, {
        motive_id: reason?.motiveId,
        note: reason?.note ?? `Reprogramada para ${newDate} ${newTime}`,
        initiated_by: reason?.initiatedBy,
        cancellation_type: 'rescheduled',
      });

      invalidateSessionsCache();
      invalidatePatientsCache();
      if (psychologistId) {
        invalidateSchedulesCache(String(psychologistId));
      }

      // Paso 3: Crear la nueva sesión en el slot seleccionado.
      // El grid se renderiza en el huso configurado del psicólogo (selectedTimeZone),
      // así que la conversión a UTC debe usar el MISMO huso — NO el del navegador.
      // Antes usaba Intl...resolvedOptions().timeZone, lo que desfasaba la sesión si
      // la psicóloga tenía el reloj del SO en otro timezone (veía 14:00 en su huso
      // configurado pero se agendaba 14:00 del huso del navegador).
      const timezone = selectedTimeZone;
      const utcDate = localTzToUTC(newDate, newTime, timezone);
      const isoDateTime = utcDate.toISOString();

      let scheduledBy = 'platform';
      try {
        const impersonatorId = getImpersonatorId();
        if (impersonatorId) {
          scheduledBy = impersonatorId;
        } else {
          const session = getSessionData();
          if (session?.user?.email) scheduledBy = session.user.email;
        }
      } catch {
        // Ignorar errores de parseo
      }

      const scheduleResult = await apiCoreAdapter.scheduleSession({
        process_id: finalProcessId,
        date: isoDateTime,
        scheduled_by: scheduledBy,
        bypass_constraints: isImpersonated(),
      });

      if (!scheduleResult.success) {
        const errorMessage = scheduleResult.reason || scheduleResult.error || 'Error al reprogramar la sesión';
        throw new Error(errorMessage);
      }
      activityTracker.trackAction("session_rescheduled"); // Métricas de Actividad: la sesión ya se canceló y se agendó la nueva

      if (!sourceSession?.patient) {
        showError('Seleccioná un paciente para continuar.');
        return;
      }

      setSessions(prev => prev.filter(session => session.id !== realSessionId));

      const ok = scheduleDirect({
        id: sourceSession.id,
        psychologistId: psychologistId,
        date: newDate,
        time: newTime,
        modality: 'online',
        patientName: sourceSession.patient.name || 'Patient',
        patientEmail: sourceSession.patient.email,
        patientPhone: sourceSession.patient.phone,
        specialty: psychologist.specialties[0] || 'general',
        status: 'scheduled',
        process_id: processId,
        appointment_type: sourceSession.appointment_type || selectedAppointmentType?.id,
        duration: sourceSession.duration,
      });

      if (ok.ok) {
        patientsCacheRef.current = undefined;
        patientsCachePsychIdRef.current = undefined;

        const endOfWeekDate = new Date(startOfWeek);
        endOfWeekDate.setDate(startOfWeek.getDate() + 6);
        const weekKey = `${startOfWeek.toISOString().slice(0, 10)}_${endOfWeekDate.toISOString().slice(0, 10)}_${psychologistId}`;
        invalidateSessionsCache(weekKey);

        const formatDdMmYyyy = (s: string): string => {
          const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
          if (m) return `${m[3]}/${m[2]}/${m[1]}`;
          try {
            const d = new Date(s);
            if (!isNaN(d.getTime())) {
              const dd = String(d.getDate()).padStart(2, '0');
              const mm = String(d.getMonth() + 1).padStart(2, '0');
              const yyyy = d.getFullYear();
              return `${dd}/${mm}/${yyyy}`;
            }
          } catch { /* noop */ }
          return s;
        };

        showSuccess(`Sesión reprogramada exitosamente para ${formatDdMmYyyy(newDate)} ${newTime}`);
        await loadData();
      }
    } catch (error) {
      console.error('Error al reprogramar sesión:', error);
      showError('Error al reprogramar la sesión. Por favor, intenta nuevamente.');
      throw error;
    } finally {
      setIsRescheduling(false);
    }
  };

  return {
    isRescheduling,
    reschedulingSession,
    setReschedulingSession,
    isPreparingReschedule,
    setIsPreparingReschedule,
    isRescheduleMode,
    unblockConfirm,
    setUnblockConfirm,
    unblockingSessionId,
    handleRefreshScheduledSession,
    handleSessionClickMemo,
    handleSessionUnblockMemo,
    handleUnblockConfirmed,
    handleRescheduleSession,
  };
}
